import { useCallback, useEffect, useRef, useState } from 'react'
import type { InferredTerritory } from '@/lib/data/territoryLocation'

export type BrowserLocationStatus =
  | 'idle'
  | 'locating'
  | 'resolved'
  | 'denied'
  | 'unavailable'
  | 'error'

export function getLocationStatusText(status: BrowserLocationStatus) {
  if (status === 'locating') return 'Detectando ubicación...'
  if (status === 'resolved') return 'Ubicación aproximada'
  if (status === 'denied') return 'Selecciona tu ciudad'
  if (status === 'unavailable') return 'Ubicación no disponible'
  if (status === 'error') return 'No se pudo detectar'
  return ''
}

export function useBrowserTerritoryLocation(enabled = true) {
  const [detectedTerritory, setDetectedTerritory] = useState<InferredTerritory | null>(null)
  const [locationStatus, setLocationStatus] = useState<BrowserLocationStatus>('idle')
  const isRequestingLocation = useRef(false)

  const requestLocation = useCallback(() => {
    if (isRequestingLocation.current) return
    isRequestingLocation.current = true

    if (!('geolocation' in navigator)) {
      setLocationStatus('unavailable')
      isRequestingLocation.current = false
      return
    }

    setLocationStatus('locating')

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { findNearestTerritory } = await import('@/lib/data/territoryLocation')
          const territory = findNearestTerritory(
            position.coords.latitude,
            position.coords.longitude,
          )

          if (!territory) {
            setLocationStatus('error')
            isRequestingLocation.current = false
            return
          }

          setDetectedTerritory(territory)
          setLocationStatus('resolved')
        } catch {
          setLocationStatus('error')
        } finally {
          isRequestingLocation.current = false
        }
      },
      (error) => {
        setLocationStatus(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable')
        isRequestingLocation.current = false
      },
      {
        enableHighAccuracy: false,
        maximumAge: 300000,
        timeout: 8000,
      },
    )
  }, [])

  useEffect(() => {
    if (enabled) requestLocation()
  }, [enabled, requestLocation])

  return {
    detectedTerritory,
    locationStatus,
    locationStatusText: getLocationStatusText(locationStatus),
    requestLocation,
  }
}
